import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  ArrowLeft,
  Building2,
  User,
  MapPin,
  Phone,
  Mail,
  FileText,
  Calendar,
  DollarSign,
  Plus,
  Edit,
} from "lucide-react";
import axios from "axios";
import { Spinner } from "../components/ui/shadcn-io/spinner";

interface ClientDetails {
  _id: string;
  clientName: string;
  contactPerson: {
    name: string;
    email: string;
    phone?: string;
    designation: string;
  };
  company: string;
  status: string;
  gstNumber: string;
  phoneNumber: string;
  dateOfOnboarding: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  industry?: string;
  clientType: string;
  notes?: string;
}

interface ProjectSummary {
  _id: string;
  projectName: string;
  status: string;
  timeline: {
    startDate: string;
    estimatedEndDate: string;
  };
  paymentStructure: {
    totalAmount: number;
    totalPaid: number;
    currency: string;
  };
}

export default function ClientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientDetails | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (id) {
      fetchClientDetails(id);
    }
  }, [id]);

  const fetchClientDetails = async (clientId: string) => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");

      // Fetch client details
      const clientResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/clients/${clientId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setClient(clientResponse.data.data.client);
      setProjects(clientResponse.data.data.projects || []);
    } catch (error: any) {
      console.error("Error fetching client details:", error);
      setError(
        error.response?.data?.message || "Failed to fetch client details"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = "INR") => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border border-green-200";
      case "inactive":
        return "bg-gray-100 text-gray-800 border border-gray-200";
      case "onhold":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const getProjectStatusStyles = (status: string) => {
    switch (status) {
      case "planning":
        return "bg-blue-100 text-blue-800 border border-blue-200";
      case "in_progress":
        return "bg-green-100 text-green-800 border border-green-200";
      case "completed":
        return "bg-purple-100 text-purple-800 border border-purple-200";
      case "on_hold":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <Spinner variant="ellipsis" size={40} className="text-primary" />
            <div className="text-center">
              <h3 className="text-lg font-medium">Loading Client Details</h3>
              <p className="text-sm text-muted-foreground">
                Fetching client information...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="rounded-lg border bg-card p-8 text-center">
            <Building2 className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Error Loading Client</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate("/clients")}>
              Back to Clients
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Client Not Found</h3>
            <p className="text-muted-foreground mb-4">
              The requested client could not be found.
            </p>
            <Button onClick={() => navigate("/clients")}>
              Back to Clients
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 pt-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-left gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/clients")}
            className="h-9 w-9 p-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Building2 className="h-6 w-6" />
              {client.clientName}
            </h1>
            <p className="text-muted-foreground flex items-center gap-2">
              <span
                className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md capitalize ${getStatusStyles(
                  client.status
                )}`}
              >
                {client.status === "onhold" ? "On Hold" : client.status}
              </span>
              • {client.clientType} •{" "}
              {client.company === "Bot2Do Technologies Pvt. Ltd"
                ? "Bot2Do"
                : "DevAnsh"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate(`/projects/add?clientId=${client._id}`)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Project
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/clients/edit/${client._id}`)}
            className="gap-2"
          >
            <Edit className="h-4 w-4" />
            Edit Client
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Client Information */}
        <div className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Client Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">GST Number:</span>
                  <span className="text-sm font-mono">{client.gstNumber}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Phone:</span>
                  <span className="text-sm">+91 {client.phoneNumber}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Onboarded:</span>
                  <span className="text-sm">
                    {new Date(client.dateOfOnboarding).toLocaleDateString()}
                  </span>
                </div>
                {client.industry && (
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Industry:</span>
                    <span className="text-sm">{client.industry}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contact Person */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Contact Person
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="font-medium">{client.contactPerson.name}</p>
                <p className="text-sm text-muted-foreground">
                  {client.contactPerson.designation}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{client.contactPerson.email}</span>
              </div>
              {client.contactPerson.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    +91 {client.contactPerson.phone}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm">
                <p>{client.address.street}</p>
                <p>
                  {client.address.city}, {client.address.state}{" "}
                  {client.address.pincode}
                </p>
                <p>{client.address.country}</p>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {client.notes && (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{client.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Projects */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Projects ({projects.length})
                </span>
                <Button
                  size="sm"
                  onClick={() =>
                    navigate(`/projects/add?clientId=${client._id}`)
                  }
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Project
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {projects.length === 0 ? (
                <div className="text-center py-8">
                  <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    No Projects Yet
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    This client doesn't have any projects yet.
                  </p>
                  <Button
                    onClick={() =>
                      navigate(`/projects/add?clientId=${client._id}`)
                    }
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Create First Project
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {projects.map((project) => (
                    <div
                      key={project._id}
                      className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/projects/${project._id}`)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium">{project.projectName}</h4>
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md capitalize ${getProjectStatusStyles(
                            project.status
                          )}`}
                        >
                          {project.status.replace("_", " ")}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Timeline:</span>
                          <div>
                            {new Date(
                              project.timeline.startDate
                            ).toLocaleDateString()}{" "}
                            -
                            {new Date(
                              project.timeline.estimatedEndDate
                            ).toLocaleDateString()}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Budget:</span>
                          <div>
                            {formatCurrency(
                              project.paymentStructure.totalAmount,
                              project.paymentStructure.currency
                            )}
                            <div className="text-xs">
                              {formatCurrency(
                                project.paymentStructure.totalPaid,
                                project.paymentStructure.currency
                              )}{" "}
                              paid
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
